<template>
  <div ref="GESTION_AEM">
    <section-header v-if="!hideTitle"
                    ref="GESTION_AEM"
                    :ressource="ressource"
                    :isExtended="extended"
                    :dialog="dialog"
                    :fiche=fiche
                    @extended=setExtended
                    @closeDialog=closeSection>
    </section-header>

    <v-row class="py-10 m-0">
      <v-col>
        <v-combobox ref="COMBO_BOX"
                    v-model=aemStatutSelected
                    class="'edit-field'"
                    :items="aemStatutList"
                    :label=labelAemStatus
                    :placeholder="placeholder"
                    :persistent-placeholder="placeholder!=''"
                    :outlined="false"
                    :ripple="false"
                    dense>
        </v-combobox>
      </v-col>
    </v-row>

    <div v-if="extended">
      <v-data-table v-model="selected"
                    :height="listHeight"
                    dense fixed-header
                    :headers="headers"
                    :items="items"
                    :items-per-page="-1"
                    :must-sort="true"
                    :mobile-breakpoint="100"
                    item-key="code"
                    class="white"
                    no-data-text="Aucune donnée"
                    :disable-pagination="true"
                    :hide-default-footer="true"
                    :show-select="true"
                    return-object
                    @input="selected">
        <template v-slot:[`item.aemDate`]="{ item }">
          {{ formatDate(item.aemDate) }}
        </template>
        <template v-slot:[`item.aemStatut`]="{ item }">
          {{ getEnumValue(item.aemStatut) }}
        </template>
      </v-data-table>
    </div>


    <div class="d-flex flex-row-reverse mt-5">
      <loader-button class='ms-2'
                     type='button'
                     :label='downloadAemLabel'
                     :loading="downloadAemLoading"
                     :disabled='downloadDisabled'
                     @callback="downloadAem">
      </loader-button>
      <loader-button class='ms-2'
                     type='button'
                     :label='importEdiLabel'
                     :loading="importEdiLoading"
                     @callback="importEdi">
      </loader-button>
      <loader-button class='ms-2'
                     type='button'
                     :label='sendEdiLabel'
                     :loading="sendEdiLoading"
                     :disabled='false'
                     @callback="sendEdi">
      </loader-button>
    </div>

    <v-dialog v-model="showEdiExportDialog" max-width="1000" scrollable>
      <v-card>
        <v-card-title>Export EDI des AEM</v-card-title>
        <v-card-text>
          <p>{{ ediExportMessage }}</p>
          <template v-if="ediExportErrors.length > 0">
            <p>Les AEM suivantes n'ont pas été incluses dans le fichier :</p>
            <v-table density="compact">
              <thead>
                <tr>
                  <th>Numéro AEM</th>
                  <th>Code contrat</th>
                  <th>Salarié</th>
                  <th>Établissement</th>
                  <th>Motif</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="error in ediExportErrors" :key="error.aemNumero + '-' + error.codeContrat">
                  <td>{{ error.aemNumero }}</td>
                  <td>{{ error.codeContrat || '—' }}</td>
                  <td>{{ error.salarie }}</td>
                  <td>{{ error.etablissement }}</td>
                  <td class="text-wrap">{{ error.message }}</td>
                </tr>
              </tbody>
            </v-table>
          </template>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn @click="showEdiExportDialog = false">Fermer</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <section-container :dialogOpen="showSection"
                       :extended="true"
                       :fullscreen="section.fullscreen"
                       :section="section"
                       @closeDialog="closeDialog">
    </section-container>
  </div>
</template>

<script lang="ts">
  import { GenSection, RessourceGenSection } from '@/generated/ressources/generatedRessources';
  import { Fiches, Sections } from '@/generated/classes/AppSchema';
  import Item from '@/framework/base/classes/Item';
  import { Extended } from '@/framework/base/classes/Schema';
  import { DateFormat } from '@/framework/base/helpers/DateFormat';
  import { ContratAemStatuts } from '@/generated/classes/Contrat';
  import SectionHeader from '@/framework/base/components/SectionHeader.vue';
  import LoaderButton from '@/framework/base/components/LoaderButton.vue';
  import AemList, { Aem, AemQuery, AemEdiExportResult } from '@/generated/classes/AemList';
  import SectionContainer from '@/framework/base/components/SectionContainer.vue';
  import Section from '@/framework/base/classes/Section';
  import ImportDialog from '@/framework/admin/components/ImportDialog.vue';
  import Files, { DownloadData } from '@/generated/classes/Files';
  import { getBlob } from '@/framework/files/helpers/FileTypes';
  import { AdminImport } from '@/generated/classes/Admin';
  import { defineComponent, type PropType } from "vue";
  import { TransactionSalarie } from "@/generated/classes/TransactionList.ts";
  import { getCurrency } from "@/framework/base/helpers/Localization.ts";

  export default defineComponent({
    name: 'SectionGestionAem',
    components: {
      SectionHeader,
      SectionContainer,
      LoaderButton
    },
    props: {
      hideTitle: {
        type: Boolean,
        required: false,
      },
      extended: {
        type: Boolean,
        required: false,
      },
      dialog: {
        type: Boolean,
        required: false,
      },
      fiche: {
        type: Object as PropType<Fiches>,
        required: true,
      },
    },
    data() {
      return {
        sectionName: Sections.GESTION_AEM,
        searchLoading: false,
        placeholder: 'Sélectionner',
        listHeight: 'calc(100vh - 500px)',
        selectedItems: [] as Aem[],
        aemList: [] as Aem[],
        aemStatut: new Item(ContratAemStatuts.EDI, 'Attestations à transmettre en fichier EDI'),
        labelAemStatus: 'Statuts des AEM',
        downloadPdfLabel: 'Télécharger les PDF',
        downloadEdiLabel: 'Télécharger le fichier EDI',
        importEdiLabel: 'Importer un compte rendu',
        sendEdiLabel: 'Déposer un fichier EDI',
        downloadAemLoading: false,
        showEdiExportDialog: false,
        ediExportResult: new AemEdiExportResult(),
        ediExportSelection: [] as Aem[],
        ediExportMessage: '',
        importEdiLoading: false,
        sendEdiLoading: false,
        showSection: false,
        section: new Section(),
      };
    },
    computed: {
      ressource(): any { return RessourceGenSection(GenSection.GESTION_AEM); },
      selected: {
        get(): Aem[] {
          return this.selectedItems;
        },
        set(res: Aem[]) {
          this.selectedItems = res;
        },
      },
      aemStatutList(): Item[] {
        const res: Item[] = [];
        res.push(new Item(-1, 'Toutes'));
        const keys = Object.keys(ContratAemStatuts).filter((v) => isNaN(Number(v)))
        keys.forEach((key, index) => {
          if (index != ContratAemStatuts.NONE) {
            let label = '';
            if (index == ContratAemStatuts.PDF) {
              label = 'Attestations à transmettre en PDF';
            } else if (index == ContratAemStatuts.EDI) {
              label = 'Attestations à transmettre en fichier EDI';
            } else if (index == ContratAemStatuts.ERROR) {
              label = 'Attestations en erreur';
            } else if (index == ContratAemStatuts.OK) {
              label = 'Attestations validées';
            }
            res.push(new Item(index, label));
          }
        })
        return res;
      },
      headers(): any {
        return [
          { title: "Code", value: 'code', cellClass: '', sortable: true },
          { title: "Etablissement", value: 'etablissementNom', cellClass: '', sortable: true },
          { title: "Prénom", value: 'salariePrenom', cellClass: '', sortable: true },
          { title: "Nom", value: 'salarieNom', cellClass: '', sortable: true },
          { title: "Emploi", value: 'emploiNom', cellClass: '', sortable: true },
          { title: "Statut AEM", value: 'aemStatut', cellClass: '', sortable: true },
          { title: "Numéro AEM", value: 'aemNumero', cellClass: '', sortable: true },
          { title: "Date", value: 'aemDate', cellClass: '', sortable: true }
        ];
      },
      items(): Aem[] {
        return this.aemList;
      },
      ediExportErrors() {
        return this.ediExportResult.errors.map(error => {
          const aem = this.ediExportSelection.find(item => item.aemNumero == error.aemNumero &&
            (error.codeContrat == 0 || item.code == error.codeContrat));
          return {
            ...error,
            codeContrat: error.codeContrat || aem?.code || 0,
            salarie: aem ? [aem.salariePrenom, aem.salarieNom].filter(Boolean).join(' ') : '—',
            etablissement: aem?.etablissementNom || '—',
          };
        });
      },
      numerosAem(): number[] {
        const numeros: number[] = [];
        this.selectedItems.forEach((aem: any) => {
          numeros.push(aem.aemNumero);
        });
        return numeros
      },
      searchDisabled(): boolean {
        return false;
      },
      downloadDisabled(): boolean {
        return this.downloadAemLoading || this.selectedItems.length == 0 ||
          (this.aemStatutSelected.key != ContratAemStatuts.PDF &&
            this.aemStatutSelected.key != ContratAemStatuts.EDI &&
          (this.selectedItems.length != 1 || this.aemStatutSelected.key != -1));
      },
      exportDisabled(): boolean {
        return this.selectedItems.length > 0;
      },
      aemStatutSelected: {
        get(): Item {
          return this.aemStatut;
        },
        set(item: Item) {
          this.aemStatut = item;
          this.search();
        },
      },
      downloadAemLabel(): any {
        let reloadEdi = false;
        if (this.aemStatutSelected.key == -1 && this.selectedItems.length == 1) {
          reloadEdi = true;
        }
        if (this.aemStatutSelected.key == ContratAemStatuts.PDF) {
          return this.downloadPdfLabel;
        }
        else if (this.aemStatutSelected.key == ContratAemStatuts.EDI || reloadEdi) {
          return this.downloadEdiLabel;
        }
        else
          return 'Choisir des attestations à transmettre';
      },
      adminImport(): AdminImport {
        const res = new AdminImport();
        res.service = 'AemCompteRendu';
        return res;
      },
    },
    created() {
      this.search();
    },
    methods: {
      loadTable(query: AemQuery): void {
        this.searchLoading = true;
        this.aemList = [];
        AemList.listAem(query).then((aem: any) => {
          this.aemList = aem;
          this.searchLoading = false;
        }).catch(() => {
          this.searchLoading = false;
        });
      },
      selected(contrats: Aem[]) {
        this.selectedItems = contrats;
      },
      formatDate(value: Date | null): string {
        return DateFormat.getDateLabel(value);
      },
      getEnumValue(value: number): string {
        let res = '';
        const keys = Object.keys(ContratAemStatuts).filter((v) => isNaN(Number(v)))
        keys.forEach((key, index) => {
          if (value == index) {
            res = key;
          }
        })
        return res;
      },
      search(): void {
        const query = new AemQuery();
        query.aemStatut = this.aemStatut.key;
        this.loadTable(query);
      },
      downloadAem(): void {
        if (this.aemStatutSelected.key == ContratAemStatuts.PDF) {
          this.downloadAemPdf();
        }
        else if (this.aemStatutSelected.key == ContratAemStatuts.EDI) {
          this.downloadAemEdi();
        }
      },
      downloadAemPdf(): void {
        const data = new DownloadData();
        this.downloadAemLoading = true;
        data.service = 'AemPdf';
        data.type.extension = 'zip';
        data.data = this.numerosAem;;

        const year = new Date().getFullYear()
        const month = new Date().getMonth() + 1;
        const strMonth = (month < 10 ? '0' : '') + month;
        const fileName = `${year}-${strMonth}-${data.service}`; // set table name as default file name

        Files.download(data)
          .then((buffer) => {
            const blob = getBlob(buffer, data.type.extension);
            const file = window.URL.createObjectURL(blob);
            const docUrl = document.createElement('a');
            docUrl.setAttribute('href', file);
            docUrl.setAttribute('download', `${fileName}.${data.type.extension}`);
            document.body.appendChild(docUrl);
            docUrl.click();
            this.downloadAemLoading = false;
          });
      },
      async downloadAemEdi(): Promise<void> {
        if (this.downloadAemLoading) return;
        this.downloadAemLoading = true;
        this.showEdiExportDialog = false;
        this.ediExportResult = new AemEdiExportResult();
        this.ediExportMessage = '';
        this.ediExportSelection = this.selectedItems.map(aem => ({ ...aem }));

        try {
          const result = await AemList.exportAemEdi(this.ediExportSelection.map(aem => aem.aemNumero));
          this.ediExportResult = result;
          if (result.successCount > 0 && result.content) {
            const date = new Date();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const fileName = date.getFullYear() + '-' + month + '-AemEdi.txt';
            const file = window.URL.createObjectURL(getBlob(result.content, 'txt'));
            const docUrl = document.createElement('a');
            try {
              docUrl.setAttribute('href', file);
              docUrl.setAttribute('download', fileName);
              document.body.appendChild(docUrl);
              docUrl.click();
            } finally {
              docUrl.remove();
              window.setTimeout(() => window.URL.revokeObjectURL(file), 1000);
            }
            this.ediExportMessage = result.successCount + ' AEM ont été incluses dans le fichier EDI. Le téléchargement a été lancé.';
          } else {
            this.ediExportMessage = "Aucune AEM n'a pu être exportée. Aucun fichier n'a été téléchargé.";
          }
          this.showEdiExportDialog = result.errors.length > 0 || result.successCount == 0;
        } catch {
          this.ediExportMessage = "Le téléchargement du fichier EDI a échoué. Veuillez réessayer.";
          this.showEdiExportDialog = true;
        } finally {
          this.downloadAemLoading = false;
        }
      },
      importEdi(): void {
        this.importEdiLoading = true;
        this.section = new Section();
        this.section.component = ImportDialog;
        this.section.type = 'config';
        this.section.data = this.adminImport;
        this.showSection = true;
        this.importEdiLoading = false;
      },
      closeDialog() {
        this.showSection = false;
      },
      sendEdi(): void {
        window.open("https://upload-aem.francetravail.fr/AEM/jsp/index.jsp", '_blank');
      },
      setExtended(extended: Extended): void {
        this.$emit('extended', extended);
      },
      closeSection(): void {
        this.$emit('closeDialog');
      },
    },
  });
</script>